import Link from 'next/link';
import * as iconSet from './iconList';
import { usePathname } from 'next/navigation';
import useTranslation from 'next-translate/useTranslation';
import { useMemo } from 'react';
import { routeInfo } from '@/lib/_utils/routing.ts';
import { BOTTOM_NAV_ALLOWED_ROUTES } from '@/lib/_utils/index.tsx';

export type IconType = keyof typeof iconSet;

export interface BottomNavItem {
	id: string;
	label: string;
	icon: IconType;
	iconActive: IconType;
	path: string;
}

const GetIcon = ({ iconKey }: { iconKey: IconType }) => {
	const Icon = iconSet[iconKey];
	if (!Icon) {
		console.warn(`Icon "${iconKey}" not found in iconSet.`);
		return <div className='text-red-500'>?</div>;
	}
	return <Icon />;
};

export const BottomNav = () => {
	const { t } = useTranslation();
	const currentPath = usePathname();

	const menuList = useMemo<BottomNavItem[]>(() => {
		return [
			{
				id: '1',
				label: routeInfo({ t, page: 'dashboard' }).label,
				icon: 'homeIcon',
				iconActive: 'homeActiveIcon',
				path: routeInfo({ t, page: 'dashboard' }).path,
			},
			{
				id: '2',
				label: routeInfo({ t, page: 'myTournaments' }).label,
				icon: 'candlestickIcon',
				iconActive: 'candlestickActiveIcon',
				path: routeInfo({ t, page: 'myTournaments' }).path,
			},
			{
				id: '3',
				label: routeInfo({ t, page: 'calendar' }).label,
				icon: 'ticketSearchIcon',
				iconActive: 'ticketSearchActiveIcon',
				path: routeInfo({ t, page: 'calendar' }).path,
			},
			{
				id: '4',
				label: routeInfo({ t, page: 'settings' }).label,
				icon: 'settingsIcon',
				iconActive: 'settingsActiveIcon',
				path: routeInfo({ t, page: 'settings' }).path,
			},
		];
	}, []);

	// Hidden bottom nav in not allowed routes
	if (!BOTTOM_NAV_ALLOWED_ROUTES.includes(currentPath)) {
		return <></>;
	}

	return (
		<div className='hidden h-[66px] lg:block'>
			<nav className='fixed bottom-0 left-0 right-0 z-50 h-[66px] w-full bg-dark-950 p-2xl shadow-[inset_0_1px_0_0] shadow-dark-700'>
				<ul className='grid grid-cols-4'>
					{menuList.map((item) => {
						const isActive = currentPath === item.path;
						const { id, path, icon, iconActive } = item;
						return (
							<li key={id}>
								<Link
									href={path}
									className={`grid justify-center gap-0.5 rounded-sm p-sm text-center ${isActive ? 'bg-dark-900' : ''}`}>
									<div className='flex justify-center'>
										<GetIcon iconKey={isActive ? iconActive : icon} />
									</div>
									<div className={`truncate text-[8px] ${isActive ? 'text-primary-500' : 'text-dark-300'}`}>
										{item.label}
									</div>
								</Link>
							</li>
						);
					})}
				</ul>
			</nav>
		</div>
	);
};

BottomNav.displayName = 'BottomNav';
